import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Lock, Bell, Settings as SettingsIcon, Bot, 
  Shield, History, Monitor, Trash2, Eye, EyeOff, Check, X,
  Smartphone, Globe, DollarSign, Palette, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import axios from 'axios';

const API_URL = '';

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  
  // Admin triple-click access
  const [headerClickCount, setHeaderClickCount] = useState(0);
  const [headerClickTimer, setHeaderClickTimer] = useState(null);
  
  // Account settings
  const [profile, setProfile] = useState({ username: '', email: '', phone: '' });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  
  // 2FA settings
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrCode, setQRCode] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAPassword, setTwoFAPassword] = useState('');
  
  // Notification settings
  const [notifications, setNotifications] = useState({
    email_deposits: true,
    email_trades: true,
    email_price_alerts: false
  });
  
  // Security settings
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [newAddress, setNewAddress] = useState({ currency: 'BTC', address: '', label: '' });
  
  // Preferences
  const [preferences, setPreferences] = useState({ currency: 'USD', theme: 'dark' });
  
  // Robot settings
  const [robotSettings, setRobotSettings] = useState({
    auto_invest: false,
    auto_invest_amount: 0,
    auto_invest_package: 'silver',
    stop_loss_enabled: false,
    stop_loss_percentage: 0
  });
  
  // Account deletion
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    loadSettings();
  }, [activeTab]);

  // Triple-click handler for header admin access (works on all devices)
  const handleHeaderAdminClick = (e) => {
    // Prevent default behavior
    e.preventDefault();
    e.stopPropagation();
    
    const newCount = headerClickCount + 1;
    setHeaderClickCount(newCount);
    
    console.log(`Admin access click: ${newCount}/3`);

    // Clear previous timer
    if (headerClickTimer) {
      clearTimeout(headerClickTimer);
    }

    // Set new timer to reset count after 1.5 seconds (longer for mobile)
    const timer = setTimeout(() => {
      setHeaderClickCount(0);
      console.log('Admin access click count reset');
    }, 1500);
    setHeaderClickTimer(timer);

    // If triple-clicked, navigate to security checkpoint
    if (newCount === 3) {
      setHeaderClickCount(0);
      clearTimeout(timer);
      console.log('Admin access granted - navigating to secure admin');
      toast({
        title: "Admin Access",
        description: "Redirecting to admin login...",
      });
      navigate('/secure-admin-access');
    }
  };

  const loadSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      switch(activeTab) {
        case 'account':
          const profileRes = await axios.get(`${API_URL}/api/settings/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (profileRes.data.success) {
            setProfile(profileRes.data.profile);
          }
          break;
          
        case 'security':
          const [twoFARes, historyRes, sessionsRes, whitelistRes] = await Promise.all([
            axios.get(`${API_URL}/api/settings/2fa/status`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_URL}/api/settings/security/login-history`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_URL}/api/settings/security/sessions`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_URL}/api/settings/security/whitelist`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          
          if (twoFARes.data.success) setTwoFAEnabled(twoFARes.data.enabled);
          if (historyRes.data.success) setLoginHistory(historyRes.data.history);
          if (sessionsRes.data.success) setActiveSessions(sessionsRes.data.sessions);
          if (whitelistRes.data.success) setWhitelist(whitelistRes.data.addresses);
          break;
          
        case 'notifications':
          const notifRes = await axios.get(`${API_URL}/api/settings/notifications`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (notifRes.data.success) {
            setNotifications(notifRes.data.notifications);
          }
          break;
          
        case 'preferences':
          const prefsRes = await axios.get(`${API_URL}/api/settings/preferences`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (prefsRes.data.success) {
            setPreferences(prefsRes.data.preferences);
          }
          break;
          
        case 'robot':
          const robotRes = await axios.get(`${API_URL}/api/settings/robot`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (robotRes.data.success) {
            setRobotSettings(robotRes.data.settings);
          }
          break;
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Account handlers
  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/settings/profile`,
        profile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({ title: 'Success', description: 'Profile updated successfully' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast({ title: 'Error', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    
    if (passwords.new.length < 8) {
      toast({ title: 'Error', description: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/auth/change-password`,
        { old_password: passwords.current, new_password: passwords.new },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: 'Success', description: 'Password changed successfully' });
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/settings/2fa/setup`,
        { password: twoFAPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setQRCode(response.data.qr_code);
        setShowQR(true);
        toast({ title: 'Success', description: 'Scan QR code with your authenticator app' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to setup 2FA',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/settings/2fa/verify`,
        { code: twoFACode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setTwoFAEnabled(true);
        setShowQR(false);
        setTwoFACode('');
        setTwoFAPassword('');
        toast({ title: 'Success', description: '2FA enabled successfully' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Invalid verification code',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/settings/2fa/disable`,
        { password: twoFAPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setTwoFAEnabled(false);
        setTwoFAPassword('');
        toast({ title: 'Success', description: '2FA disabled successfully' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to disable 2FA',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Notification handler
  const handleUpdateNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/settings/notifications`,
        notifications,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({ title: 'Success', description: 'Notification settings updated' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to update notifications',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Whitelist handler
  const handleAddWhitelist = async () => {
    if (!newAddress.address) {
      toast({ title: 'Error', description: 'Please enter an address', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/settings/security/whitelist`,
        newAddress,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({ title: 'Success', description: 'Address added to whitelist' });
        setNewAddress({ currency: 'BTC', address: '', label: '' });
        loadSettings();
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to add address',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveWhitelist = async (addressId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/settings/security/whitelist/${addressId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: 'Success', description: 'Address removed from whitelist' });
      loadSettings();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to remove address',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Preferences handler
  const handleUpdatePreferences = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/settings/preferences`,
        preferences,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({ title: 'Success', description: 'Preferences updated' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to update preferences',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Robot handler
  const handleUpdateRobot = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/settings/robot`,
        robotSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({ title: 'Success', description: 'Robot settings updated' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to update robot settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Account deletion handler
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE MY ACCOUNT') {
      toast({ 
        title: 'Error', 
        description: 'Please type "DELETE MY ACCOUNT" to confirm',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/settings/account/delete`,
        { password: deletePassword, confirmation: deleteConfirmation },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({ title: 'Account Deleted', description: 'Your account has been deleted' });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-cyan-950 to-blue-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-br from-slate-950/95 via-blue-950/95 to-cyan-950/95 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-cyan-300">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-black">BACK</span>
          </button>
          <h1 className="text-lg font-black text-white">SETTINGS</h1>
          {/* Hidden Admin Access - Triple click/tap to access admin panel */}
          <button 
            onClick={handleHeaderAdminClick}
            onTouchEnd={handleHeaderAdminClick}
            className="w-8 h-8 opacity-20 hover:opacity-100 transition-opacity active:scale-95"
            title="Triple tap for admin access"
            aria-label="Admin access"
            type="button"
          >
            <Shield className="w-5 h-5 text-cyan-600" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4 py-6">
        <TabsList className="w-full grid grid-cols-5 gap-1 mb-6 bg-slate-900/50">
          <TabsTrigger value="account" className="text-xs">
            <User className="w-4 h-4 mr-1" />
            Account
          </TabsTrigger>
          <TabsTrigger value="security" className="text-xs">
            <Shield className="w-4 h-4 mr-1" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs">
            <Bell className="w-4 h-4 mr-1" />
            Notify
          </TabsTrigger>
          <TabsTrigger value="preferences" className="text-xs">
            <SettingsIcon className="w-4 h-4 mr-1" />
            Prefs
          </TabsTrigger>
          <TabsTrigger value="robot" className="text-xs">
            <Bot className="w-4 h-4 mr-1" />
            Robot
          </TabsTrigger>
        </TabsList>

        {/* Account Tab - Continued in next message due to length */}
        <TabsContent value="account" className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-cyan-400" />
              PROFILE
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2">Username</Label>
                <Input
                  value={profile.username}
                  onChange={(e) => setProfile({...profile, username: e.target.value})}
                  className="bg-slate-800 border-cyan-500/30 text-white"
                />
              </div>
              
              <div>
                <Label className="text-white mb-2">Email</Label>
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({...profile, email: e.target.value})}
                  className="bg-slate-800 border-cyan-500/30 text-white"
                />
              </div>
              
              <div>
                <Label className="text-white mb-2">Phone</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({...profile, phone: e.target.value})}
                  placeholder="+1234567890"
                  className="bg-slate-800 border-cyan-500/30 text-white"
                />
              </div>
              
              <Button
                onClick={handleUpdateProfile}
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-400 to-cyan-600 hover:opacity-90 text-white font-bold"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-cyan-400" />
              CHANGE PASSWORD
            </h2>
            
            <div className="space-y-4">
              <div className="relative">
                <Label className="text-white mb-2">Current Password</Label>
                <Input
                  type={showPasswords.current ? "text" : "password"}
                  value={passwords.current}
                  onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                  className="bg-slate-800 border-cyan-500/30 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                  className="absolute right-3 top-9 text-cyan-400"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              <div className="relative">
                <Label className="text-white mb-2">New Password</Label>
                <Input
                  type={showPasswords.new ? "text" : "password"}
                  value={passwords.new}
                  onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                  className="bg-slate-800 border-cyan-500/30 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                  className="absolute right-3 top-9 text-cyan-400"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              <div className="relative">
                <Label className="text-white mb-2">Confirm New Password</Label>
                <Input
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                  className="bg-slate-800 border-cyan-500/30 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                  className="absolute right-3 top-9 text-cyan-400"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              <Button
                onClick={handleChangePassword}
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-400 to-cyan-600 hover:opacity-90 text-white font-bold"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>

          {/* Account Deletion */}
          <div className="bg-gradient-to-br from-red-900/30 to-slate-900/80 border border-red-500/30 rounded-xl p-6">
            <h2 className="text-lg font-black text-red-400 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              DELETE ACCOUNT
            </h2>
            
            <p className="text-white text-sm mb-4">
              This action is permanent and cannot be undone.
            </p>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2">Type "DELETE MY ACCOUNT"</Label>
                <Input
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="DELETE MY ACCOUNT"
                  className="bg-slate-800 border-red-500/30 text-white"
                />
              </div>
              
              <div>
                <Label className="text-white mb-2">Password</Label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="bg-slate-800 border-red-500/30 text-white"
                />
              </div>
              
              <Button
                onClick={handleDeleteAccount}
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90 text-white font-bold"
              >
                {loading ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Security Tab - Add all remaining tabs */}
        <TabsContent value="security" className="space-y-4">
          <p className="text-white text-sm">Security features coming soon: 2FA, Login History, Sessions, Whitelist</p>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-cyan-400" />
              EMAIL NOTIFICATIONS
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-bold">Deposits</Label>
                  <p className="text-cyan-400 text-xs">Get notified when deposits are confirmed</p>
                </div>
                <Switch
                  checked={notifications.email_deposits}
                  onCheckedChange={(checked) => setNotifications({...notifications, email_deposits: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-bold">Trades</Label>
                  <p className="text-cyan-400 text-xs">Get notified about trade executions</p>
                </div>
                <Switch
                  checked={notifications.email_trades}
                  onCheckedChange={(checked) => setNotifications({...notifications, email_trades: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-bold">Price Alerts</Label>
                  <p className="text-cyan-400 text-xs">Get notified about price changes</p>
                </div>
                <Switch
                  checked={notifications.email_price_alerts}
                  onCheckedChange={(checked) => setNotifications({...notifications, email_price_alerts: checked})}
                />
              </div>
              
              <Button
                onClick={handleUpdateNotifications}
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-400 to-cyan-600 hover:opacity-90 text-white font-bold"
              >
                {loading ? 'Updating...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-cyan-400" />
              CURRENCY
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2">Display Currency</Label>
                <select
                  value={preferences.currency}
                  onChange={(e) => setPreferences({...preferences, currency: e.target.value})}
                  className="w-full h-12 bg-slate-800 border border-cyan-500/30 text-white rounded-md px-3"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="JPY">JPY (¥)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-cyan-400" />
              THEME
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2">Theme Mode</Label>
                <select
                  value={preferences.theme}
                  onChange={(e) => setPreferences({...preferences, theme: e.target.value})}
                  className="w-full h-12 bg-slate-800 border border-cyan-500/30 text-white rounded-md px-3"
                >
                  <option value="dark">Dark (Cyberpunk)</option>
                  <option value="light">Light</option>
                </select>
              </div>
              
              <Button
                onClick={handleUpdatePreferences}
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-400 to-cyan-600 hover:opacity-90 text-white font-bold"
              >
                {loading ? 'Updating...' : 'Save Preferences'}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="robot" className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <Bot className="w-5 h-5 text-cyan-400" />
              AUTOMATION
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-bold">Auto-Invest</Label>
                  <p className="text-cyan-400 text-xs">Automatically reinvest profits</p>
                </div>
                <Switch
                  checked={robotSettings.auto_invest}
                  onCheckedChange={(checked) => setRobotSettings({...robotSettings, auto_invest: checked})}
                />
              </div>
              
              {robotSettings.auto_invest && (
                <>
                  <div>
                    <Label className="text-white mb-2">Amount</Label>
                    <Input
                      type="number"
                      value={robotSettings.auto_invest_amount || ''}
                      onChange={(e) => setRobotSettings({...robotSettings, auto_invest_amount: parseFloat(e.target.value)})}
                      placeholder="100"
                      className="bg-slate-800 border-cyan-500/30 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white mb-2">Package</Label>
                    <select
                      value={robotSettings.auto_invest_package}
                      onChange={(e) => setRobotSettings({...robotSettings, auto_invest_package: e.target.value})}
                      className="w-full h-12 bg-slate-800 border border-cyan-500/30 text-white rounded-md px-3"
                    >
                      <option value="silver">Silver</option>
                      <option value="gold">Gold</option>
                      <option value="diamond">Diamond</option>
                      <option value="platinum">Platinum</option>
                      <option value="vip">VIP Elite</option>
                    </select>
                  </div>
                </>
              )}
              
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-bold">Stop Loss</Label>
                  <p className="text-cyan-400 text-xs">Automatically stop when losing</p>
                </div>
                <Switch
                  checked={robotSettings.stop_loss_enabled}
                  onCheckedChange={(checked) => setRobotSettings({...robotSettings, stop_loss_enabled: checked})}
                />
              </div>
              
              {robotSettings.stop_loss_enabled && (
                <div>
                  <Label className="text-white mb-2">Stop Loss %</Label>
                  <Input
                    type="number"
                    value={robotSettings.stop_loss_percentage || ''}
                    onChange={(e) => setRobotSettings({...robotSettings, stop_loss_percentage: parseFloat(e.target.value)})}
                    placeholder="10"
                    className="bg-slate-800 border-cyan-500/30 text-white"
                  />
                </div>
              )}
              
              <Button
                onClick={handleUpdateRobot}
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-400 to-cyan-600 hover:opacity-90 text-white font-bold"
              >
                {loading ? 'Updating...' : 'Save Robot Settings'}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
